import axios from "axios";
import { getSession } from "../auth/session";

export const AUTH_EXPIRED_EVENT = "attar:session-expired";

export const isCanceled = (error) =>
  error?.code === "ERR_CANCELED" || error?.name === "AbortError";

const canceled = () => Object.assign(new Error("Request canceled"), {
  name: "AbortError",
  code: "ERR_CANCELED",
});

export function retryAfterMs(value, now = Date.now()) {
  if (value === undefined || value === null || value === "") return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(value);
  return Number.isNaN(date) ? null : Math.max(0, date - now);
}

function delay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(canceled());
    const abort = () => {
      clearTimeout(timer);
      reject(canceled());
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, ms);
    signal.addEventListener("abort", abort, { once: true });
  });
}

export class ApiError extends Error {
  constructor(error) {
    const status = error.response?.status ?? null;
    const data = error.response?.data;
    const validation = data?.errors || data?.messages;
    const fields = data && typeof data === "object" ? Object.entries(data).filter(([, value]) => Array.isArray(value)).map(([key, value]) => key + ": " + value.join("، ")).join("؛ ") : "";
    const fallback = status === 429
      ? "درخواست‌های زیادی ارسال شده است. کمی صبر کنید."
      : status === 401
        ? "لطفاً دوباره وارد سامانه شوید."
        : status === 403
          ? "اجازه انجام این درخواست را ندارید."
          : status >= 500
            ? "مشکلی در سرور پیش آمده است."
            : status === null
              ? "ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی کنید."
              : "درخواست انجام نشد.";
    super(
      (Array.isArray(validation) && validation.join("، ")) ||
      (typeof data?.detail === "string" && data.detail) ||
      (typeof data?.message === "string" && data.message) || fields ||
      (typeof data?.error === "string" && data.error) ||
      (typeof data?.status === "string" && data.status) ||
      fallback
    );
    this.name = "ApiError";
    this.status = status;
    this.data = data;
    this.response = error.response;
    this.code = error.code;
    this.retryAfterMs = retryAfterMs(error.response?.headers?.["retry-after"]);
  }
}

export function createApiClient({
  baseURL,
  getToken = () => null,
  onUnauthorized = () => {},
  wait = delay,
}) {
  const pending = new Map();
  let invalidatedToken = null;

  const request = async ({
    url, method = "GET", data, params, headers = {}, signal,
    auth = true, safeRead = false, responseType = "json",
  }) => {
    if (signal?.aborted) return Promise.reject(canceled());
    if (!baseURL) return Promise.reject(new Error("API base URL is not configured."));
    const base = new URL(baseURL.replace(/\/+$/, "") + "/");
    const resolved = new URL(url.replace(/^\/+/, ""), base);
    if (resolved.origin !== base.origin || resolved.username || resolved.password) {
      return Promise.reject(new Error("API requests must use the configured origin."));
    }
    if (process.env.NODE_ENV === "production" && resolved.protocol !== "https:") {
      return Promise.reject(new Error("Production API requests require HTTPS."));
    }
    const token = auth ? getToken() : null;
    const requestHeaders = { Accept: "application/json" };
    Object.entries(headers || {}).forEach(([key, value]) => {
      if (key.toLowerCase() !== "authorization") requestHeaders[key] = value;
    });
    if (token) requestHeaders.Authorization = "Token " + token;
    const verb = method.toUpperCase();
    const canRetry = safeRead && verb === "GET" && data == null;
    const config = {
      url: resolved.href, method: verb, data, params, headers: requestHeaders,
      responseType, withCredentials: false, timeout: 30000,
    };
    const key = canRetry ? JSON.stringify([token, config]) : null;
    let entry = key ? pending.get(key) : null;

    if (!entry) {
      const controller = new AbortController();
      entry = { controller, consumers: 0, settled: false };
      const run = async () => {
        for (let attempt = 0; ; attempt += 1) {
          if (controller.signal.aborted || (token && token !== getToken())) {
            throw canceled();
          }
          try {
            const result = await axios.request({ ...config, signal: controller.signal });
            if (controller.signal.aborted || (token && token !== getToken())) {
              throw canceled();
            }
            return result;
          } catch (error) {
            if (isCanceled(error) || controller.signal.aborted ||
                (token && token !== getToken())) throw canceled();
            const failure = new ApiError(error);
            if (failure.status === 401 && token && invalidatedToken !== token) {
              invalidatedToken = token;
              onUnauthorized();
            }
            if (!canRetry || failure.status !== 429 || attempt >= 2) throw failure;
            await wait(failure.retryAfterMs ?? 1000 * 2 ** attempt, controller.signal);
          }
        }
      };
      const current = entry;
      entry.promise = run().finally(() => {
        current.settled = true;
        if (key && pending.get(key) === current) pending.delete(key);
      });
      if (key) pending.set(key, entry);
    }

    const current = entry;
    current.consumers += 1;
    return new Promise((resolve, reject) => {
      let done = false;
      const finish = (callback, value) => {
        if (done) return;
        done = true;
        signal?.removeEventListener("abort", abort);
        current.consumers -= 1;
        callback(value);
        if (!current.consumers && !current.settled) {
          if (key && pending.get(key) === current) pending.delete(key);
          current.controller.abort();
        }
      };
      const abort = () => finish(reject, canceled());
      current.promise.then(
        (result) => finish(resolve, result),
        (error) => finish(reject, error)
      );
      signal?.addEventListener("abort", abort, { once: true });
      if (signal?.aborted) abort();
    });
  };

  return { request };
}

export const api = createApiClient({
  baseURL: process.env.REACT_APP_API_URL,
  getToken: () => getSession()?.token,
  onUnauthorized: () => window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT)),
});
